/** @file Dependency list of one compiled animation: fps, draw order, index maps, every entity it keys. */
import type { AnimationDoc, AttachmentDoc, Doc } from '@pose2d/format/types';

/** A deformed attachment, then its socket and image when mounted (its offsets turn through the mount), else nulls. */
function deformDeps(doc: Doc, att: AttachmentDoc | undefined): [unknown, unknown, unknown] {
  const mount = att?.mount;
  return mount === undefined ? [att, null, null] : [att, doc.sockets[mount.socket], doc.images[att?.image ?? '']];
}

/** Canonical index maps an animation compiles against (identity-stable while their contents are). */
export type AnimationMaps = { bone: unknown; slot: unknown; ik: unknown; attachment: unknown; event: unknown };

// compileAnimations reads fps, the maps, the animation and each deformed mesh, plus a mounted mesh's socket and image
// (its offsets turn through the mount, whose scale reads the socket length and whose handle frame reads the image
// size). The keyed bones, slots, constraints, fired events and the setup draw order are a deliberate conservative
// superset (see the G.2 memo table). The list has the same length for the same animation object (three entries per
// deformed mesh, null when unmounted), so entries compare positionally (sameDeps).
/** Memo deps of one compiled animation: fps, draw order, the five maps, then every entity it keys or deforms. */
export function animationDeps(doc: Doc, anim: AnimationDoc, maps: AnimationMaps): unknown[] {
  const deps: unknown[] = [doc.meta.fps, doc.drawOrder, maps.bone, maps.slot, maps.ik, maps.attachment, maps.event];
  for (const id of Object.keys(anim.bones)) deps.push(doc.bones[id]);
  for (const id of Object.keys(anim.slots)) deps.push(doc.slots[id]);
  for (const id of Object.keys(anim.ik)) deps.push(doc.constraints[id]);
  for (const id of Object.keys(anim.deform)) deps.push(...deformDeps(doc, doc.attachments[id]));
  for (const e of anim.events) deps.push(doc.events[e.event]);
  return deps;
}
